import {
  CHANNEL_NAME,
  didAsyncTaskFail,
  isAccessGroupManageMembersTask,
  isAsyncTaskChannelFinished,
  isAsyncTaskChannelUpdated,
  isPaywallMembersMigrationTask,
} from "@/react/helpers/asyncTaskHelpers";
import { useWebSocket } from "@/react/hooks/useWebSocket";
import { usePunditUserContext } from "@circle-react/contexts";
import { accessGroupPaths } from "@circle-react/helpers/urlHelpers/accessGroupPaths";
import { queryClient } from "@circle-react/providers";
import { CustomProgressToast } from "@circle-react-shared/uikit/ToastV2";
import type { CustomProgressToastProps } from "@circle-react-shared/uikit/ToastV2/CustomProgressToast";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useAccessGroupData } from "../AccessGroupMembers/DataStoreContext/AccessGroupDataContext";
import { useCustomProgressToast } from "./useCustomProgressToast";

export interface Message {
  id: number;
  message: string;
  params: {
    process_type: string;
    access_group_id: number;
    access_group_ids: number[];
  };
  progress: string;
  status: string;
  type: string;
}

export interface EventData {
  event: string;
  record: string;
}

export const useAccessGroupWebSocket = () => {
  const { currentCommunityMember } = usePunditUserContext();

  const { toastsIdsMap, setToastsIdsMap, updateToastProgress } =
    useCustomProgressToast();

  const { setShouldBlockActions, shouldBlockActions } = useAccessGroupData();

  const { error: errorToast, show } = useToast();

  const currentPath = window.location.pathname;

  // Extract ID from the current URL path
  const idFromUrl = currentPath.split("/").pop();

  const isCurrentPathAccessGroup = (groupId: number | undefined) => {
    if (!groupId) {
      return false;
    }

    const accessGroupEditPath = accessGroupPaths.edit({
      accessGroupId: groupId,
    });

    return accessGroupEditPath === currentPath;
  };

  const onMessageReceive = async (eventData: EventData) => {
    const { event, record } = eventData;
    const parsedRecord = JSON.parse(record) as Message;
    const { id: toastIdNumber, params } = parsedRecord;
    const toastId = String(toastIdNumber);

    const currentAccessGroupId =
      params?.access_group_id ||
      (params?.access_group_ids || []).find(
        (id: number) => id === Number(idFromUrl),
      );

    if (
      !isAccessGroupManageMembersTask(parsedRecord?.type) &&
      !isPaywallMembersMigrationTask(parsedRecord?.type)
    ) {
      return;
    }

    const shouldHideToast = !isCurrentPathAccessGroup(currentAccessGroupId);

    if (!toastsIdsMap[toastId]) {
      setToastsIdsMap(prevState => {
        show("", {
          id: String(toastId),
          shouldAutohide: false,
          render: (args: CustomProgressToastProps) => (
            <CustomProgressToast {...args} />
          ),
        });
        return { ...prevState, [toastId]: true };
      });
    }

    if (isAsyncTaskChannelUpdated(event)) {
      if (!shouldBlockActions) {
        setShouldBlockActions(true);
      }

      updateToastProgress({
        toastId,
        // TODO: If the backend sends a progress field, we can use it in the ProgressIcon progress prop
        customProgress: 0,
        message: parsedRecord.progress,
        shouldHideToast,
      });
    } else if (isAsyncTaskChannelFinished(event)) {
      if (didAsyncTaskFail(parsedRecord.status)) {
        errorToast(parsedRecord.message);
        updateToastProgress({
          toastId,
          customProgress: 100,
          shouldHideToast: true,
        });
      } else {
        updateToastProgress({
          toastId,
          customProgress: 100,
          message: parsedRecord.message,
          shouldHideToast,
        });
      }
      setShouldBlockActions(false);
    }
    await queryClient.invalidateQueries("community-members");
    await queryClient.invalidateQueries(["access_groups", "show"]);
  };

  useWebSocket({
    channel: CHANNEL_NAME,
    onMessageReceive: onMessageReceive,
    canCreateConnection: !!currentCommunityMember?.id,
    community_member_id: currentCommunityMember?.id,
  });
};
